/**
 * EmojiPickerButton — smiley toolbar button opening a searchable emoji picker.
 * Used in the rich-text formatting toolbar (posts, comments, announcements)
 * and the DM composer. The heavy picker library is lazy-loaded on first open.
 */
import React, { Suspense, lazy, useState } from 'react';
import { Smile, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// A stale tab after a deploy can miss the rotated chunk; degrade to a note
// instead of throwing into the top-level error page from the reactions path.
const PickerUnavailable = () => (
  <div className="w-[320px] px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400 bg-white dark:bg-[#2d2f33] rounded-lg border border-slate-200 dark:border-white/10" data-testid="emoji-picker-unavailable">
    Couldn't load the emoji picker. Refresh the page and try again.
  </div>
);
const Picker = lazy(() => import('emoji-picker-react').catch(() => ({ default: PickerUnavailable })));

// 380 px unless the viewport is short (phone in landscape, small window).
const pickerHeight = () => Math.max(240, Math.min(380, (typeof window !== 'undefined' ? window.innerHeight : 700) - 120));

/**
 * The searchable picker itself, lazy-loaded, themed, fixed 320×380 so the
 * loading placeholder and the loaded picker occupy the same box. Rendered
 * inline by the reaction pickers (posts, comments, DMs) and inside the
 * toolbar popover below.
 */
export const LazyEmojiPicker = ({ onEmojiSelect, autoFocusSearch = false }) => {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const height = pickerHeight();
  return (
    <Suspense
      fallback={
        <div className="w-[320px] flex items-center justify-center bg-white dark:bg-[#2d2f33] rounded-lg border border-slate-200 dark:border-white/10" style={{ height }}>
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      }
    >
      <Picker
        onEmojiClick={(emojiData) => onEmojiSelect(emojiData.emoji)}
        theme={isDark ? 'dark' : 'light'}
        width={320}
        height={height}
        autoFocusSearch={autoFocusSearch}
        lazyLoadEmojis
        skinTonesDisabled={false}
        previewConfig={{ showPreview: false }}
        searchPlaceHolder="Search emoji..."
      />
    </Suspense>
  );
};

export const EmojiPickerButton = ({ onEmojiSelect, className = '', iconClassName = 'w-4 h-4', title = 'Insert emoji', side = 'bottom' }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={title}
          onMouseDown={(e) => e.preventDefault()}
          className={className}
          data-testid="emoji-picker-btn"
        >
          <Smile className={iconClassName} />
        </button>
      </PopoverTrigger>
      <PopoverContent side={side} align="start" className="p-0 w-auto border-0 bg-transparent shadow-xl" data-testid="emoji-picker-popover">
        <LazyEmojiPicker
          onEmojiSelect={(emoji) => {
            onEmojiSelect(emoji);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default EmojiPickerButton;
